"use client";

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { clarityIdentify, claritySetTag } from "@/lib/analytics";
import { setAnalyticsUserId } from "@/lib/analytics-events";

interface AuthUser {
  id: number;
  userID: string;
  username: string;
  email: string;
}

export interface EmailSuppressionInfo {
  suppressed: true;
  bounceType: "hard_bounce" | "spam_complaint" | "user_opt_out" | "soft_bounce";
  reason: string;
  lastBounceAt: string;
  bounceCount: number;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  emailSuppression: EmailSuppressionInfo | null;
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
  refreshSuppression: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  emailSuppression: null,
  login: () => {},
  logout: () => {},
  refreshSuppression: async () => {},
});

interface MeResponse {
  user: AuthUser | null;
  emailSuppression: EmailSuppressionInfo | null;
}

async function fetchMe(token: string): Promise<MeResponse> {
  const res = await fetch("/api/auth/me", { headers: { Authorization: `Bearer ${token}` } });
  const data = await res.json();
  if (data && data.encrypted) {
    const { decrypt } = await import("@/lib/security/client-encrypt");
    return decrypt<MeResponse>(data.data);
  }
  return data as MeResponse;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [emailSuppression, setEmailSuppression] = useState<EmailSuppressionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      setLoading(false);
      return;
    }

    fetchMe(token)
      .then((data) => {
        if (data.user) {
          setUser(data.user);
          setAnalyticsUserId(data.user.id);
          setEmailSuppression(data.emailSuppression ?? null);
          clarityIdentify(data.user.id, data.user.username);
          claritySetTag("login_status", "logged_in");
        } else {
          localStorage.removeItem("auth_token");
          setAnalyticsUserId(null);
          claritySetTag("login_status", "logged_out");
        }
        setLoading(false);
      })
      .catch(() => {
        localStorage.removeItem("auth_token");
        setLoading(false);
      });
  }, []);

  const login = (token: string, user: AuthUser) => {
    localStorage.setItem("auth_token", token);
    setUser(user);
    setAnalyticsUserId(user.id);
    clarityIdentify(user.id, user.username);
    claritySetTag("login_status", "logged_in");
    // 登录后异步拉一次 suppression(不阻塞 UI)
    fetchMe(token)
      .then((data) => setEmailSuppression(data.emailSuppression ?? null))
      .catch(() => {});
  };

  const logout = () => {
    // 服务端撤销（tokenVersion++），失败也不阻塞前端清理
    const token = localStorage.getItem("auth_token");
    if (token) {
      fetch("/api/auth/logout", { method: "POST", headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
    localStorage.removeItem("auth_token");
    setUser(null);
    setAnalyticsUserId(null);
    setEmailSuppression(null);
    claritySetTag("login_status", "logged_out");
  };

  const refreshSuppression = useCallback(async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) return;
    try {
      const data = await fetchMe(token);
      setEmailSuppression(data.emailSuppression ?? null);
    } catch {
      // 静默,context 保留旧值
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, emailSuppression, login, logout, refreshSuppression }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * 获取认证 token（供 secureFetch 使用）
 */
export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("auth_token");
}
